/**
 * Synapse UserHub — 機能ガイド & トラブルシュート（SSOT）
 */

import { findNavItemByPath, resolveFallbackNavPath } from '../utils/hubNavVisibility.js';

export const FEATURE_CATEGORIES = [
  {
    id: 'auth-account',
    title: 'ログイン・アカウント',
    icon: 'key',
    features: [
      {
        id: 'login',
        title: 'ログイン',
        openPath: null,
        description: 'クリニック名・ユーザーID・パスワードでログインします。',
        steps: [
          'ログイン画面でクリニック名・ユーザーID・パスワードを入力する',
          '「ログイン」をクリックする',
          '成功するとダッシュボードへ移動します',
        ],
      },
      {
        id: 'signup',
        title: '新規登録',
        openPath: null,
        description: '新しいクリニックとしてアカウントを作成します。クリニック情報と担当者情報の入力が必要です。',
        steps: [
          '新規登録画面でクリニック情報を入力する',
          '担当者（最初のユーザー）情報を入力する',
          '確認メールの案内に従って登録を完了する',
        ],
      },
      {
        id: 'reset-password',
        title: 'パスワード変更',
        openPath: '/settings/account',
        description: 'ログイン中のアカウントのパスワードを変更できます。',
        steps: [
          'アカウント設定画面を開く',
          '「パスワード変更」で現在のパスワードと新しいパスワードを入力する',
          '保存する',
        ],
      },
      {
        id: 'logout',
        title: 'ログアウト',
        openPath: null,
        description: 'ログアウトします。',
        steps: [
          'サイドバー下部のログアウトボタン（矢印アイコン）をクリックする',
          'ログイン画面へ戻ります',
        ],
      },
      {
        id: 'notifications',
        title: '通知の確認',
        openPath: '/dashboard?open=notifications',
        description: 'お知らせ・契約関連の通知を確認できます。',
        steps: [
          '右上のベルアイコンをクリックする',
          '一覧から通知を選んで内容を確認する',
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'ダッシュボード',
    icon: 'layout',
    features: [
      {
        id: 'dashboard-overview',
        title: 'ダッシュボード概要',
        openPath: '/dashboard',
        description: '契約中のモジュール、クリニックの利用状況、支払いに関する警告を一覧で確認できます。',
        steps: [
          'サイドバー「ダッシュボード」をクリックする',
          '利用中モジュール・ユーザー数・契約状況を確認する',
        ],
      },
      {
        id: 'module-launch',
        title: 'モジュールの起動',
        openPath: '/modules',
        description: '契約中の各製品モジュールを起動し、製品画面へ移動します。',
        steps: [
          'ダッシュボードまたはモジュール画面で起動したい製品を探す',
          '「起動」ボタンをクリックする',
          '製品画面へ自動的に移動します（初回は数秒かかることがあります）',
        ],
      },
      {
        id: 'payment-warning',
        title: '支払い方法の警告',
        openPath: '/billing?tab=methods',
        description: 'クレジットカードが未登録の場合、画面上部に警告が表示されます。',
        steps: [
          '警告バナーの内容を確認する',
          '「決済・請求」画面から支払い方法を登録する',
        ],
      },
      {
        id: 'clinic-summary',
        title: 'クリニック概要',
        openPath: '/dashboard',
        description: '登録ユーザー数や契約モジュール数など、クリニック全体の概要を確認できます。',
        steps: [
          'ダッシュボードの各カードで数値を確認する',
          '詳細はライセンス画面やユーザー管理画面へ進む',
        ],
      },
    ],
  },
  {
    id: 'modules',
    title: 'モジュール',
    icon: 'grid',
    features: [
      {
        id: 'modules-list',
        title: 'モジュール一覧',
        openPath: '/modules',
        description: '利用中・期限切れ・追加可能なモジュールが区分表示されます。',
        steps: [
          'サイドバー「モジュール」をクリックする',
          '各セクション（利用中 / 期限切れ / 追加可能）を確認する',
        ],
      },
      {
        id: 'modules-trial',
        title: '無料トライアル',
        openPath: '/licenses',
        description: '対象モジュールは無料トライアル期間中に利用できます。トライアル終了後は支払い方法の登録が必要です。',
        steps: [
          'ライセンス画面でトライアル対象のモジュールを選ぶ',
          'トライアル開始を確認する',
          '残日数はライセンス画面で確認できます',
        ],
      },
      {
        id: 'modules-expired',
        title: '期限切れモジュール',
        openPath: '/licenses',
        description: '契約が終了したモジュールは起動できません。再契約が必要です。',
        steps: [
          'モジュール画面の「期限切れ」セクションを確認する',
          'ライセンス画面から再契約する',
        ],
      },
    ],
  },
  {
    id: 'licenses',
    title: 'ライセンス',
    icon: 'shield',
    features: [
      {
        id: 'license-view',
        title: '契約状況の確認',
        openPath: '/licenses',
        description: '各モジュールの契約状態・トライアル残日数・解約予定日を確認できます。',
        steps: [
          'サイドバー「ライセンス」をクリックする',
          '各製品カードで契約状態を確認する',
        ],
      },
      {
        id: 'license-add',
        title: 'ライセンスの追加',
        openPath: '/licenses',
        description: '新しいモジュールの契約を追加します。医師アカウントでの操作が必要です。',
        steps: [
          '追加したいモジュールの「契約する」をクリックする',
          '内容を確認して確定する',
          '契約は即時反映され、モジュール画面から起動できます',
        ],
      },
      {
        id: 'license-cancel',
        title: 'ライセンスの解約',
        openPath: '/licenses',
        description: '不要になったモジュールの解約手続きを行います。解約後も当月末までは利用できます。',
        steps: [
          '解約するモジュールの「解約」をクリックする',
          '解約理由を選択して確定する',
          '解約予定日はライセンス画面で確認できます',
        ],
      },
      {
        id: 'license-role',
        title: '契約操作の権限',
        openPath: '/licenses',
        description: 'ライセンスの追加・解約は医師アカウントのみ行えます。その他のスタッフは閲覧のみです。',
        steps: [
          '契約操作が必要な場合は、医師アカウントでログインする',
          '医師アカウントの方に依頼して操作してもらう',
        ],
      },
      {
        id: 'license-trial-end',
        title: 'トライアル終了後',
        openPath: '/billing?tab=methods',
        description: 'トライアル終了後、支払い方法が登録されていれば自動的に有料契約へ移行します。',
        steps: [
          'トライアル終了前に決済・請求画面でカードを登録する',
          'トライアル中に解約すれば料金は発生しません',
          'トライアル終了月は日割り請求、翌月から定額請求となります',
        ],
      },
    ],
  },
  {
    id: 'billing',
    title: '決済・請求',
    icon: 'credit-card',
    features: [
      {
        id: 'billing-overview',
        title: '請求概要',
        openPath: '/billing?tab=overview',
        description: '次回請求日・月額合計（税別見積）・契約中モジュールの請求内訳を確認できます。未払いがある場合は税込の確定請求額も表示されます。',
        steps: [
          'サイドバー「決済・請求」をクリックする',
          '概要タブで次回請求情報（税別見積／未払い時は税込）を確認する',
        ],
      },
      {
        id: 'billing-history',
        title: '請求履歴',
        openPath: '/billing?tab=history',
        description: '過去の請求・領収書（税込の実額）を確認できます。',
        steps: [
          '決済・請求画面の「請求履歴」タブを開く',
          '各請求の詳細を確認する',
        ],
      },
      {
        id: 'billing-payment-method',
        title: '支払い方法の登録・変更',
        openPath: '/billing?tab=methods',
        description: 'クレジットカードの登録・変更は、安全な外部の支払い画面で行います。',
        steps: [
          '決済・請求画面の「支払い方法」タブを開く',
          '「支払い方法を管理」をクリックする',
          'カード情報の登録・変更画面で手続きする',
        ],
      },
      {
        id: 'billing-invoice',
        title: '請求書・領収書',
        openPath: '/billing?tab=history',
        description: '請求書・領収書は支払い設定画面から PDF で取得できます。',
        steps: [
          '請求履歴から対象の請求を選ぶ',
          '支払い設定画面で PDF をダウンロードする',
        ],
      },
    ],
  },
  {
    id: 'settings',
    title: 'クリニック・ユーザー設定',
    icon: 'users',
    features: [
      {
        id: 'account-settings',
        title: 'アカウント設定',
        openPath: '/settings/account',
        description: '表示名・パスワード・メール通知の設定を変更できます。',
        steps: [
          'サイドバー「アカウント」をクリックする',
          '変更したい項目を編集して保存する',
        ],
      },
      {
        id: 'clinic-settings',
        title: 'クリニック設定',
        openPath: '/settings/clinic',
        description: 'クリニック名・住所・連絡先・ロゴを確認できます。編集は医師アカウントのみ可能です。',
        steps: [
          'サイドバー「クリニック」をクリックする',
          '医師アカウントで各項目を編集し「保存」をクリックする',
        ],
      },
      {
        id: 'app-integrations',
        title: 'アプリ連携',
        openPath: '/settings/integrations',
        description: 'PlumConnect など外部サービスとの連携用アカウント・アクセスキーを登録します。保存時に連携確認を行います。',
        steps: [
          'サイドバー「アプリ連携」をクリックする',
          'PlumConnect のアカウントとアクセスキーを入力する',
          '「保存して連携確認」をクリックし、確認に成功すると保存されます',
        ],
      },
      {
        id: 'users-invite',
        title: 'ユーザーの追加',
        openPath: '/settings/users?open=invite',
        description: '新しいメンバーのアカウント（ユーザーID・パスワード・職種）を作成できます。操作は医師アカウントのみ可能です。',
        steps: [
          'ユーザー管理画面で「ユーザーを追加」をクリックする',
          'ユーザーID・表示名・パスワード・職種を入力する',
          '追加完了画面でログイン情報をコピーし、本人に伝える',
        ],
      },
      {
        id: 'users-roles',
        title: '職種（ロール）の種類',
        openPath: '/settings/users',
        description: '歯科医師・歯科衛生士・歯科助手などの職種を設定します。ユーザー管理の編集と契約操作は医師アカウントのみ可能です。',
        steps: [
          'ユーザー追加または編集画面で職種を選択する',
          '自分の操作権限が不明な場合は医師アカウントの方に確認する',
        ],
      },
      {
        id: 'users-remove',
        title: 'ユーザーの無効化・削除',
        openPath: '/settings/users',
        description: '退職したメンバーのアカウントを無効化または削除できます。操作は医師アカウントのみ可能です。',
        steps: [
          'ユーザー一覧から対象ユーザーを選ぶ',
          '「無効化」または「削除」を実行する（医師アカウントのみ操作可能）',
        ],
      },
      {
        id: 'invitation-code',
        title: '招待・紹介コード',
        openPath: '/settings/invitation',
        description: '紹介コードの生成・登録により特典を受け取れます。',
        steps: [
          '招待コード画面でコードを生成または入力する',
          '表示される案内に従って登録する',
        ],
      },
    ],
  },
  {
    id: 'products-pricing',
    title: '製品・料金',
    icon: 'package',
    features: [
      {
        id: 'products-overview',
        title: '製品ラインナップ',
        openPath: '/products',
        description: 'Synapseシリーズの各歯科 SaaS モジュールの概要を確認できます。',
        steps: [
          '「製品」ページから各モジュールの説明を読む',
          '気になる製品はライセンス画面からトライアルまたは契約する',
        ],
      },
      {
        id: 'pricing-plans',
        title: '料金プラン',
        openPath: '/pricing',
        description: 'モジュール別の料金・Enterprise プランなどを確認できます。',
        steps: [
          '料金ページで各プランを比較する',
          'ライセンス画面から契約手続きを行う',
        ],
      },
      {
        id: 'pricing-faq',
        title: '料金に関する FAQ',
        openPath: '/pricing#faq',
        description: 'トライアル・解約・バックアップ・データ移行など、料金に関するよくある質問があります。',
        steps: [
          '料金ページ下部の「よくあるご質問」を確認する',
          '不明点はお問い合わせフォームから連絡する',
        ],
      },
    ],
  },
  {
    id: 'support-legal',
    title: 'お問い合わせ・規約',
    icon: 'help',
    features: [
      {
        id: 'contact-hub',
        title: 'お問い合わせ',
        openPath: '/support/contact',
        description: 'ご質問やご要望を送信できます。クリニック情報は自動で入力されます。',
        steps: [
          'サイドバー「お問い合わせ」をクリックする',
          'カテゴリと内容を入力して送信する',
        ],
      },
      {
        id: 'contact-public',
        title: 'Webサイトのお問い合わせ',
        openPath: '/contact',
        description: 'アカウントをお持ちでない方向けの、Webサイト上のお問い合わせフォームです。',
        steps: [
          'Webサイト下部の「お問い合わせ」からアクセスする',
          '必要事項を入力して送信する',
        ],
      },
      {
        id: 'help-tools',
        title: 'ヘルプ・機能ガイド（この画面）',
        openPath: null,
        description: '操作方法の確認や、うまくいかないときの対処方法を調べられます。',
        steps: [
          '右下の本のアイコンで機能ガイドとトラブルシュートを開く',
          'キーワード検索やカテゴリで絞り込む',
        ],
      },
      {
        id: 'legal',
        title: '規約・ポリシー',
        openPath: '/support/legal/terms',
        openPathLabel: '利用規約を開く',
        openPathAlt: '/support/legal/privacy',
        openPathAltLabel: 'プライバシーポリシーを開く',
        openPathExtra: '/support/legal/commerce',
        openPathExtraLabel: '特定商取引法に基づく表記を開く',
        description: '利用規約、プライバシーポリシー、特定商取引法に基づく表記を確認できます。',
        steps: [
          'サイドバー「規約・ポリシー」から一覧を開くこともできます',
          '確認したい文書を選んで開く',
        ],
      },
    ],
  },
];

export const TROUBLESHOOTING_FAQ = [
  {
    id: 'login-fail',
    category: 'ログイン',
    severity: 'high',
    title: 'ログインに失敗する',
    openPath: '/support/contact',
    symptoms: [
      'クリニック名・ID・パスワードを入力してもログインできない',
      '「認証に失敗しました」と表示される',
    ],
    causes: [
      '入力情報の誤り（全角/半角、大文字小文字）',
      'パスワードの期限切れ・未設定',
      'ブラウザの Cookie がブロックされている',
      'アカウントが無効化されている',
    ],
    solutions: [
      'クリニック名・ユーザーID・パスワードをもう一度確認する',
      '「パスワードをお忘れですか？」から再設定する',
      'シークレットモードや別ブラウザで試す',
      '院内の医師アカウントの方に、アカウント状態を確認してもらう',
      '解決しない場合はお問い合わせフォームから連絡する',
    ],
    relatedFeatures: ['login', 'reset-password', 'contact-hub'],
  },
  {
    id: 'session-expired',
    category: 'ログイン',
    severity: 'medium',
    title: '操作中にログアウトされる',
    symptoms: [
      '作業中に突然ログイン画面に戻される',
      '「セッションが切れました」と表示される',
    ],
    causes: [
      '長時間操作がなかった',
      '別の端末でログアウトした',
      'ブラウザの Cookie が削除された',
    ],
    solutions: [
      '再度ログインする',
      'ブラウザの Cookie を有効にする',
      '作業中は定期的に保存する',
    ],
    relatedFeatures: ['login', 'logout'],
  },
  {
    id: 'signup-email',
    category: '登録',
    severity: 'medium',
    title: '確認メールが届かない',
    symptoms: [
      '新規登録後、確認メールが届かない',
      'メール内のリンクが無効になっている',
      'メール未確認のためログインできない',
    ],
    causes: [
      'メールアドレスの入力ミス',
      '迷惑メールフォルダに振り分けられている',
      'メールサーバーの受信遅延',
      '確認リンクの有効期限（24時間）切れ',
    ],
    solutions: [
      '入力したメールアドレスを確認する',
      '迷惑メールフォルダを確認する',
      'ログイン画面の「確認メールが届かない方はこちら」から再送する',
      '届かない場合はお問い合わせフォームから連絡する',
    ],
    relatedFeatures: ['signup', 'contact-public'],
  },
  {
    id: 'module-not-launch',
    category: 'モジュール',
    severity: 'high',
    title: 'モジュールが起動しない',
    symptoms: [
      '「起動」ボタンを押しても製品画面に移らない',
      '起動ボタンがグレーアウトしている',
    ],
    causes: [
      'ライセンス未契約・期限切れ',
      'トライアル終了後に支払い方法が未登録',
      '解約予定で利用期間が終了している',
      'ブラウザのポップアップブロック',
    ],
    solutions: [
      'ライセンス画面で該当モジュールの契約状態を確認する',
      '支払い方法が未登録なら決済・請求からカードを登録する',
      '解約予定の場合は再契約が必要です',
      'ポップアップブロックを解除して再試行する',
      'ページを再読み込みする',
    ],
    relatedFeatures: ['module-launch', 'license-view', 'billing-payment-method'],
  },
  {
    id: 'module-expired',
    category: 'モジュール',
    severity: 'medium',
    title: '「ライセンスが無効」と表示される',
    symptoms: [
      'モジュール起動時にライセンスエラーが出る',
      'モジュール一覧で「期限切れ」と表示される',
    ],
    causes: [
      '契約が解約済みまたは期限切れ',
      'トライアル期間が終了し支払い方法が未登録',
      '請求の失敗により契約が停止された',
    ],
    solutions: [
      'ライセンス画面で契約状態を確認する',
      '必要なら再契約する',
      '支払い方法を確認・更新する',
    ],
    relatedFeatures: ['modules-expired', 'license-add', 'billing-payment-method'],
  },
  {
    id: 'license-no-permission',
    category: 'ライセンス',
    severity: 'medium',
    title: 'ライセンスの追加・解約ができない',
    symptoms: [
      '「契約する」「解約」ボタンが表示されない、または操作できない',
      '権限がない旨のメッセージが表示される',
    ],
    causes: [
      '医師アカウント以外でログインしている',
      'β版期間中の機能制限',
    ],
    solutions: [
      '医師アカウントでログインし直す',
      '医師アカウントの方に契約操作を依頼する',
      'β版制限の場合はサイドバー下部の β 切替を確認する',
    ],
    relatedFeatures: ['license-role', 'license-add', 'license-cancel'],
  },
  {
    id: 'trial-charge',
    category: 'ライセンス',
    severity: 'medium',
    title: 'トライアル中に料金が請求された',
    symptoms: [
      'トライアル中なのに請求が発生した',
      '想定より高い金額が請求された',
    ],
    causes: [
      'トライアル期間が終了している',
      '複数モジュールの同時契約',
      '日割り請求が発生した月',
    ],
    solutions: [
      'ライセンス画面でトライアル残日数を確認する',
      '決済・請求画面で請求内訳を確認する',
      '不明点はお問い合わせフォームから連絡する',
    ],
    relatedFeatures: ['license-trial-end', 'billing-overview', 'pricing-faq'],
  },
  {
    id: 'payment-method-fail',
    category: '決済',
    severity: 'high',
    title: 'クレジットカードを登録できない',
    symptoms: [
      'Stripe の画面でカード登録に失敗する',
      '「支払い方法を登録してください」と表示され続ける',
    ],
    causes: [
      'カード情報の入力ミス',
      'カードの利用限度額超過',
      '3D セキュア認証の失敗',
      'β版期間中の決済機能制限',
    ],
    solutions: [
      'カード番号・有効期限・セキュリティコードを再確認する',
      '別のカードで試す',
      'β版制限の場合は β 切替を OFF にする（権限がある場合）',
      '解決しない場合はお問い合わせフォームから連絡する',
    ],
    relatedFeatures: ['billing-payment-method', 'payment-warning'],
  },
  {
    id: 'billing-unexpected',
    category: '決済',
    severity: 'medium',
    title: '請求金額が想定と異なる',
    symptoms: [
      '請求書の金額が期待と違う',
      '解約したのに請求がある',
    ],
    causes: [
      '解約月の日割り請求',
      '複数モジュールの同時契約',
      'トライアル終了月の日割り請求',
    ],
    solutions: [
      '決済・請求画面で請求内訳を確認する',
      'ライセンス画面で解約予定日を確認する',
      '料金ページの FAQ を参照する',
      '不明点はお問い合わせフォームから連絡する',
    ],
    relatedFeatures: ['billing-overview', 'billing-history', 'pricing-faq'],
  },
  {
    id: 'user-invite-fail',
    category: 'ユーザー',
    severity: 'medium',
    title: '追加したユーザーがログインできない',
    symptoms: [
      '作成したユーザーIDでログインできない',
      'パスワードが合わない',
    ],
    causes: [
      'ユーザーID・パスワード・クリニック名の入力ミス',
      'アカウントが無効化されている',
      'ログイン情報の共有漏れ',
    ],
    solutions: [
      '追加完了画面でコピーしたログイン情報を再確認する',
      'ユーザー管理画面で対象ユーザーが有効か確認する',
      '医師アカウントの担当者に再確認またはアカウント再作成を依頼する',
    ],
    relatedFeatures: ['users-invite'],
  },
  {
    id: 'user-role-confusion',
    category: 'ユーザー',
    severity: 'low',
    title: 'ロールの違いがわからない',
    symptoms: [
      '自分にどの権限があるかわからない',
      '操作できない機能がある',
    ],
    causes: [
      'ロールごとに利用できる機能が異なる',
      '契約操作は医師アカウントのみ',
    ],
    solutions: [
      'ユーザー管理画面で自分のロールを確認する',
      '契約・解約が必要な場合は医師アカウントの方に依頼する',
      '機能ガイドの「契約操作の権限」を参照する',
    ],
    relatedFeatures: ['users-roles', 'license-role'],
  },
  {
    id: 'clinic-info-not-saved',
    category: '設定',
    severity: 'low',
    title: 'クリニック情報の変更が反映されない',
    symptoms: [
      '保存したはずの情報が元に戻っている',
      'ロゴが表示されない',
    ],
    causes: [
      '保存前にページを離れた',
      '画像ファイル形式・サイズの問題',
      '一時的な通信エラー',
      '編集権限がない（保存ボタンがグレーアウトしている）',
    ],
    solutions: [
      '医師アカウントでログインし「保存」をクリックする',
      'ロゴは推奨形式（PNG/WebP）・サイズ以内か確認する',
      'ページを再読み込みして確認する',
    ],
    relatedFeatures: ['clinic-settings'],
  },
  {
    id: 'beta-restriction',
    category: 'β版',
    severity: 'medium',
    title: '「β版期間中はご利用いただけません」と表示される',
    openPath: '/contact',
    symptoms: [
      '決済・請求やお問い合わせが使えない',
      'サイドバーの項目がグレーアウトしている',
    ],
    causes: [
      'β版期間中のため、一部機能が制限されている',
    ],
    solutions: [
      '表示されている場合は、サイドバー下部の「β切替」を OFF にする',
      '本番運用開始後は自動的に制限が解除されます',
      '急ぎの場合は Webサイトのお問い合わせフォームから送る',
    ],
    relatedFeatures: ['billing-overview', 'contact-public'],
  },
  {
    id: 'page-stuck',
    category: '操作',
    severity: 'low',
    title: '画面が読み込みのまま進まない',
    symptoms: [
      '読み込み中の表示がずっと続く',
      'ボタンを押しても反応しない',
    ],
    causes: [
      '通信環境の問題',
      'ブラウザの一時的な不具合',
      '大量データの読み込み中',
    ],
    solutions: [
      'しばらく待ってから再度操作する',
      'ページを再読み込みする',
      '別のブラウザで試す',
      '改善しない場合はお問い合わせフォームから連絡する',
    ],
    relatedFeatures: ['dashboard-overview'],
  },
  {
    id: 'data-migration',
    category: '移行',
    severity: 'low',
    title: '既存システムからデータを移行したい',
    symptoms: [
      '以前使っていたシステムのデータを引き継ぎたい',
      '移行方法がわからない',
    ],
    causes: [
      '製品・データ形式によって移行方法が異なる',
    ],
    solutions: [
      '料金ページの FAQ「既存のシステムからデータを移行できますか？」を確認する',
      'お問い合わせフォームから移行希望の旨を連絡する',
      'サポートチームがクリニックの状況に応じて案内します',
    ],
    relatedFeatures: ['pricing-faq', 'contact-hub'],
  },
  {
    id: 'product-howto',
    category: '製品',
    severity: 'low',
    title: '各モジュールの使い方を知りたい',
    symptoms: [
      '各製品モジュール側の操作方法がわからない',
      '患者登録や画像アップロードなどの方法を知りたい',
    ],
    causes: [
      '製品ごとに操作画面が異なる',
      'この画面は契約・起動の管理が主な役割で、製品ごとの操作は各モジュール内で行う',
    ],
    solutions: [
      'モジュールを起動して製品画面へ移動する',
      '各製品画面のヘルプ（機能ガイド）を参照する',
      '製品固有の問い合わせはお問い合わせフォームから連絡する',
    ],
    relatedFeatures: ['module-launch', 'help-tools'],
  },
];

export const PATH_CONTEXT_MAP = [
  {
    pathPrefix: '/dashboard',
    featureIds: ['dashboard-overview', 'module-launch', 'notifications', 'payment-warning'],
    faqIds: ['module-not-launch', 'payment-method-fail'],
  },
  {
    pathPrefix: '/modules',
    featureIds: ['modules-list', 'module-launch', 'modules-trial'],
    faqIds: ['module-not-launch', 'module-expired'],
  },
  {
    pathPrefix: '/licenses',
    featureIds: ['license-view', 'license-add', 'license-cancel', 'license-role'],
    faqIds: ['license-no-permission', 'trial-charge', 'module-expired'],
  },
  {
    pathPrefix: '/billing',
    featureIds: ['billing-overview', 'billing-payment-method', 'billing-history'],
    faqIds: ['payment-method-fail', 'billing-unexpected'],
  },
  {
    pathPrefix: '/settings/account',
    featureIds: ['account-settings'],
    faqIds: ['session-expired'],
  },
  {
    pathPrefix: '/settings/clinic',
    featureIds: ['clinic-settings'],
    faqIds: ['clinic-info-not-saved'],
  },
  {
    pathPrefix: '/settings/integrations',
    featureIds: ['app-integrations'],
    faqIds: [],
  },
  {
    pathPrefix: '/settings/users',
    featureIds: ['users-invite', 'users-roles'],
    faqIds: ['user-invite-fail', 'user-role-confusion'],
  },
  {
    pathPrefix: '/settings/invitation',
    featureIds: ['invitation-code'],
    faqIds: [],
  },
  {
    pathPrefix: '/support/contact',
    featureIds: ['contact-hub'],
    faqIds: ['beta-restriction'],
  },
  {
    pathPrefix: '/support/legal',
    featureIds: ['legal'],
    faqIds: [],
  },
];

const featureIndex = new Map();
for (const cat of FEATURE_CATEGORIES) {
  for (const f of cat.features) {
    featureIndex.set(f.id, { ...f, categoryId: cat.id, categoryTitle: cat.title });
  }
}

const faqIndex = new Map(TROUBLESHOOTING_FAQ.map((f) => [f.id, f]));

export function getFeatureById(id) {
  return featureIndex.get(id) || null;
}

export function getFaqById(id) {
  return faqIndex.get(id) || null;
}

/** β版中にブロックされる Hub 内パスを公開お問い合わせへフォールバック */
export function resolveOpenPath(openPath, { effectiveIsBeta, userHubNavVisibility } = {}) {
  if (!openPath) return null;
  const base = openPath.split('?')[0].split('#')[0];
  if (effectiveIsBeta && (base === '/billing' || base === '/support/contact')) {
    return '/contact';
  }
  if (userHubNavVisibility && typeof userHubNavVisibility === 'object') {
    const item = findNavItemByPath(base);
    if (item && userHubNavVisibility[item.id] === false) {
      return resolveFallbackNavPath(userHubNavVisibility);
    }
  }
  return openPath;
}

export function resolveFeatureOpenPath(feature, betaOpts) {
  return resolveOpenPath(feature?.openPath, betaOpts);
}

/** FAQ の「開く」先（明示 openPath または relatedFeatures から解決） */
export function getFaqOpenPath(faq, betaOpts) {
  if (!faq) return null;
  if (faq.openPath !== undefined) {
    return faq.openPath === null ? null : resolveOpenPath(faq.openPath, betaOpts);
  }
  for (const fid of faq.relatedFeatures || []) {
    const resolved = resolveFeatureOpenPath(getFeatureById(fid), betaOpts);
    if (resolved) return resolved;
  }
  return null;
}

export function getContextHelpForPath(pathname) {
  const path = pathname || '';
  const matches = PATH_CONTEXT_MAP.filter((entry) => path.startsWith(entry.pathPrefix));
  if (matches.length === 0) {
    return { features: [], faqs: [] };
  }
  const best = matches.sort((a, b) => b.pathPrefix.length - a.pathPrefix.length)[0];
  return {
    features: best.featureIds.map((id) => getFeatureById(id)).filter(Boolean),
    faqs: best.faqIds.map((id) => getFaqById(id)).filter(Boolean),
  };
}

export function searchFeatures(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return FEATURE_CATEGORIES;

  return FEATURE_CATEGORIES.map((cat) => ({
    ...cat,
    features: cat.features.filter(
      (f) =>
        f.title.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q) ||
        (f.steps || []).some((s) => s.toLowerCase().includes(q))
    ),
  })).filter((cat) => cat.features.length > 0);
}

export function searchFaq(query, categoryFilter) {
  const q = (query || '').trim().toLowerCase();
  return TROUBLESHOOTING_FAQ.filter((item) => {
    if (categoryFilter && item.category !== categoryFilter) return false;
    if (!q) return true;
    return (
      item.title.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q) ||
      item.symptoms.some((s) => s.toLowerCase().includes(q)) ||
      item.causes.some((c) => c.toLowerCase().includes(q)) ||
      item.solutions.some((s) => s.toLowerCase().includes(q))
    );
  });
}

export const FAQ_CATEGORIES = [...new Set(TROUBLESHOOTING_FAQ.map((f) => f.category))];
